import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaGlobeAfrica, FaHandHoldingHeart, FaEye, FaStar } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getPublicPage } from '../services/siteContentService';
import { useTranslation } from 'react-i18next';
import { normalizePublicLink, usePublicLocale } from '../routes/publicPaths';

const DEFAULT_CONTENT = {
  intro: { title: 'عن الجمعية', body: 'جمعية صحبة الخير جمعية مغربية غير ربحية.' },
  scope: { title: 'نطاقنا', body: 'نعمل في مختلف جهات المملكة المغربية.' },
  mission: { title: 'رسالتنا', body: 'نسعى لتقديم يد العون للمحتاجين.' },
  vision: { title: 'رؤيتنا', body: 'أن نكون منارة للعمل الخيري الشفاف.' },
  values: [{ title: 'الإخلاص والجودة' }, { title: 'العدالة' }, { title: 'الشفافية' }],
  cta: { title: 'انضم إلينا', body: 'ساهم معنا', button_text: 'اكتشف برامجنا', button_link: '/programmes' },
};

const AboutPage = () => {
  const { t } = useTranslation();
  const locale = usePublicLocale();
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    getPublicPage('about')
      .then((page) => setContent(page.content))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${content.intro?.title || t('public.nav.whoWeAre')} | ${t('public.brand.titleSuffix')}`}</title>
        <meta name="description" content={(content.intro?.body || '').slice(0, 160)} />
        <html lang={locale} />
      </Helmet>
      <Navbar />
      <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <section className="mb-16">
          <div className="bg-white rounded-xl shadow-md p-8 text-center max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{content.intro?.title}</h1>
            <p className="text-gray-700 leading-relaxed text-lg">{content.intro?.body}</p>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-white rounded-xl shadow-md p-8 max-w-3xl mx-auto text-center">
            <div className="bg-brand-surface w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaGlobeAfrica className="text-brand-primary text-3xl" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">{content.scope?.title}</h2>
            <p className="text-gray-600">{content.scope?.body}</p>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-brand-cta rounded-xl shadow-lg p-8 max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl font-semibold mb-4">{content.mission?.title}</h2>
            <p className="leading-relaxed">{content.mission?.body}</p>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-r from-brand-secondary to-brand-primary rounded-xl shadow-lg p-8 max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl font-semibold mb-4">{content.vision?.title}</h2>
            <p className="leading-relaxed">{content.vision?.body}</p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">{t('public.about.valuesHeading')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(content.values || []).map((value, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="text-3xl mb-3">
                  {index % 3 === 0 ? <FaStar className="text-brand-yellow inline" /> : index % 3 === 1 ? <FaHandHoldingHeart className="text-brand-primary inline" /> : <FaEye className="text-brand-green inline" />}
                </div>
                <h3 className="text-lg font-medium text-gray-800">{value.title}</h3>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{content.cta?.title}</h2>
          <p className="text-gray-600 mb-6">{content.cta?.body}</p>
          <Link to={normalizePublicLink(content.cta?.button_link || '/programmes', locale)} className="inline-block bg-brand-primary hover:bg-brand-primary-dark text-white font-medium py-3 px-8 rounded-lg">
            {content.cta?.button_text || t('public.about.discoverPrograms')}
          </Link>
        </section>
      </div>
      </div>
    </div>
  );
};

export default AboutPage;
